import type { PriceLevel } from '@/feature/order_book/types.ts';
import { formatNumberDisplay } from '@/utils/bigUtils';

const MAX_L2_PRICE_PRECISION = 14;
const FP_SNAP_EPSILON = 1e-9;

export const roundBidToTickSize = (price: number, tickSizeNum: number): number => {
  const ratio = price / tickSizeNum;
  // Snap to nearest integer when within floating-point error to avoid
  // e.g. 61149.7 / 0.1 = 611496.9999999999 flooring to 611496 instead of 611497
  const rounded = Math.round(ratio);
  const corrected = Math.abs(ratio - rounded) < FP_SNAP_EPSILON ? rounded : ratio;
  const result = Math.floor(corrected) * tickSizeNum;
  return Number(result.toPrecision(MAX_L2_PRICE_PRECISION));
};

export const roundAskToTickSize = (price: number, tickSizeNum: number): number => {
  const ratio = price / tickSizeNum;
  const rounded = Math.round(ratio);
  const corrected = Math.abs(ratio - rounded) < FP_SNAP_EPSILON ? rounded : ratio;
  const result = Math.ceil(corrected) * tickSizeNum;
  return Number(result.toPrecision(MAX_L2_PRICE_PRECISION));
};

export const createPriceLevel = (
  priceNum: number,
  sizeNum: number,
  quoteDecimals: number,
  assetDecimals: number,
  version: number
): PriceLevel => {
  const totalNum = priceNum * sizeNum;

  return {
    price: formatNumberDisplay(priceNum, { decimals: quoteDecimals }),
    size: formatNumberDisplay(sizeNum, { decimals: assetDecimals }),
    priceNum,
    sizeNum,
    total: formatNumberDisplay(totalNum, { decimals: 0 }),
    totalNum,
    version,
    // Caller (LevelAggregator) overwrites these with running sums; defaulting to 0 keeps the
    // type satisfied for standalone construction (tests, future ad-hoc callers).
    cumulativeSizeNum: 0,
    cumulativeTotalNum: 0,
  };
};
